// Shared contract with www.skiloope.com — keep both copies behaviorally identical.
// The cookie lives on .skiloope.com so one choice covers www and app.

export type Consent = 'granted' | 'denied';

export const CONSENT_COOKIE = 'skiloope_consent';
export const OPEN_COOKIE_SETTINGS_EVENT = 'skiloope:open-cookie-settings';

const LEGACY_CONSENT_COOKIE = 'cookie_consent';
const SECONDS_PER_DAY = 86400;
const CONSENT_LIFETIME_DAYS = 365;
const SECONDS_PER_YEAR = CONSENT_LIFETIME_DAYS * SECONDS_PER_DAY;
const COOKIE_DOMAIN = '.skiloope.com';

// _ga, _ga_<id>, _gid, _gat*, _gac_*, _gcl_au, _gcl_aw, … and every Hotjar _hj*.
const TRACKING_COOKIE_PATTERN = /^(_ga(_.*)?|_gid|_gat.*|_gac_.*|_gcl_.*|_hj.*)$/;

// A Domain attribute for a host it doesn't cover (localhost, preview URLs)
// makes the browser drop the cookie, so only send it on skiloope.com hosts.
const isSkiloopeHost = (): boolean => {
  const { hostname } = window.location;
  return hostname === 'skiloope.com' || hostname.endsWith(COOKIE_DOMAIN);
};

const readCookie = (name: string): string | null => {
  const entry = document.cookie.split('; ').find(pair => pair.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : null;
};

const expireCookie = (name: string, domain?: string): void => {
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Max-Age=0; Path=/${domainPart}`;
};

export const getConsent = (): Consent | null => {
  const value = readCookie(CONSENT_COOKIE);
  return value === 'granted' || value === 'denied' ? value : null;
};

export const setConsent = (value: Consent): void => {
  const attributes = ['Path=/', `Max-Age=${SECONDS_PER_YEAR}`, 'SameSite=Lax'];
  if (isSkiloopeHost()) attributes.push(`Domain=${COOKIE_DOMAIN}`);
  if (window.location.protocol === 'https:') attributes.push('Secure');
  document.cookie = `${CONSENT_COOKIE}=${value}; ${attributes.join('; ')}`;
};

// The old cookie only covered analytics, so it never counts as consent — drop it.
export const removeLegacyConsent = (): void => {
  if (readCookie(LEGACY_CONSENT_COOKIE) !== null) expireCookie(LEGACY_CONSENT_COOKIE);
};

// Google/Hotjar cookies may be host-only or shared on .skiloope.com; clear both.
export const clearTracking = (): void => {
  document.cookie.split('; ').forEach(pair => {
    const name = pair.split('=')[0];
    if (!TRACKING_COOKIE_PATTERN.test(name)) return;
    expireCookie(name);
    if (isSkiloopeHost()) expireCookie(name, COOKIE_DOMAIN);
  });
};

export const openCookieSettings = (): void => {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};
